import { Chip } from '@jotpsych/ui';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import { episodeStatusColor, episodeStatusLabel } from '../labels';
import {
  composeSig,
  isInactiveEpisode,
  resolveDateCell,
  resolvePrescriber,
} from '../vm/formatters';
import type { MedicationEpisodeVM, MedicationWorkspaceVM } from '../vm/types';

const PAST_CAP = 3;

interface MedicationPeekListProps {
  workspace: MedicationWorkspaceVM;
  /** Opens the full edit modal (the peek itself is read-only). */
  onEdit?: () => void;
}

const PeekRow: React.FC<{ episode: MedicationEpisodeVM }> = ({ episode }) => {
  const regimen = episode.actualUse ?? episode.prescribed;
  const prescriber = resolvePrescriber(episode);
  const caption = [episode.target, prescriber].filter(Boolean).join(' · ');

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'flex-start', justifyContent: 'space-between', py: 0.75 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {episode.drugName}
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {composeSig(regimen)}
          </Box>
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {caption}
          </Typography>
        )}
      </Box>
      <Chip
        variant="soft"
        size="xSmall"
        color={episodeStatusColor[episode.episodeStatus]}
        label={episodeStatusLabel[episode.episodeStatus]}
        sx={{ flexShrink: 0, mt: 0.25 }}
      />
    </Stack>
  );
};

/**
 * Compact hover-peek medication list for the patient context bar (the popover
 * paper is 400–600px). Speaks the unified-list vocabulary: per-med status
 * chips (Current / On hold / Stopped), indication and Sent-by when known, and
 * past meds capped with a count — so status reads the same here as on the
 * patient-page card. Read-only; Edit opens the full modal via the host.
 */
export const MedicationPeekList: React.FC<MedicationPeekListProps> = ({
  workspace,
  onEdit,
}) => {
  const current = workspace.episodes.filter((episode) => !isInactiveEpisode(episode));
  const past = workspace.episodes.filter(isInactiveEpisode);
  const shownPast = past.slice(0, PAST_CAP);

  return (
    <Box sx={{ minWidth: 400, maxWidth: 600, p: 2 }}>
      {/* Header */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <MedicationOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Medications
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {workspace.episodes.length} total · {current.length} active
        </Typography>
        {onEdit && (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onEdit}
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            Edit
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* Current regimen — uncapped */}
      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {workspace.episodes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No medications on file.
          </Typography>
        )}
        {current.map((episode) => (
          <PeekRow key={episode.id} episode={episode} />
        ))}

        {/* Past — capped */}
        {past.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.disabled',
              }}
            >
              Past
            </Typography>
            {shownPast.map((episode) => {
              const dateCell = resolveDateCell(episode);
              return (
                <Stack
                  key={episode.id}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {episode.drugName}
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {' '}
                      · {dateCell.caption} {dateCell.value}
                    </Box>
                  </Typography>
                  <Chip
                    variant="soft"
                    size="xSmall"
                    color={episodeStatusColor[episode.episodeStatus]}
                    label={episodeStatusLabel[episode.episodeStatus]}
                    sx={{ flexShrink: 0 }}
                  />
                </Stack>
              );
            })}
            {past.length > PAST_CAP && (
              <Typography variant="caption" color="text.disabled">
                …and {past.length - PAST_CAP} more
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
